import type { ApiResponse, Concept, ProblemType, ProblemTypeRelation, ProblemTypeNode, ConceptGraphStats } from '../types';
import { getErrorMessage } from '../types';
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin } from './fetchHelper';

/** Shared write helper — POST/DELETE with admin auth + 401 handling. */
async function writeJson<T>(method: 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const token = authApi.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) {
    authApi.logout();
    throw new Error('Session expired. Please login again.');
  }
  const data: ApiResponse<T> = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(getErrorMessage(data.error) || `API error: ${res.status} ${res.statusText}`);
  }
  return data.data as T;
}

export const conceptsApi = {
  listConcepts: (opts?: { subject?: string; gateableOnly?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.subject) params.set('subject', opts.subject);
    if (opts?.gateableOnly) params.set('gateable', 'true');
    const qs = params.toString();
    return fetchWithAdmin<{ concepts: Concept[]; total: number }>(`/concepts${qs ? `?${qs}` : ''}`);
  },
  getConcept: (id: string) =>
    fetchWithAdmin<Concept>(`/concepts/${id}`),
  saveConcept: (c: Partial<Concept>) =>
    writeJson<Concept>('POST', '/concepts', c),
  deleteConcept: (id: string) =>
    writeJson<{ deleted: boolean }>('DELETE', `/concepts/${id}`),
  // Alias auto-suggest: match free text (e.g. a solution's steps) → candidate concepts.
  suggestConcepts: (subject: string, text: string) =>
    fetchWithAdmin<{ concepts: Concept[] }>(
      `/concepts/suggest?subject=${encodeURIComponent(subject)}&text=${encodeURIComponent(text)}`,
    ),
  // Problem types for a subject — nested under their concept in the hierarchy view.
  listProblemTypes: (subject: string) =>
    fetchWithAdmin<{ problemTypes: ProblemType[]; total: number }>(
      `/problem-types?subject=${encodeURIComponent(subject)}`,
    ),
  // Type-level navigation edges for a subject — overlaid on each type in the hierarchy.
  listProblemTypeRelations: (subject: string) =>
    fetchWithAdmin<{ relations: ProblemTypeRelation[]; total: number }>(
      `/problem-type-relations?subject=${encodeURIComponent(subject)}`,
    ),
  // One node for the graph traverser: the type + member questions + outgoing edges.
  getProblemTypeNode: (id: string, subject: string) =>
    fetchWithAdmin<ProblemTypeNode>(
      `/problem-types/${encodeURIComponent(id)}?subject=${encodeURIComponent(subject)}`,
    ),
  // Health of the concept/problem-type substrate. Computed live per call — there is no
  // cached snapshot, so refetching genuinely re-measures. Omit `subject` for all subjects.
  getConceptGraphStats: (subject?: string) =>
    fetchWithAdmin<ConceptGraphStats>(
      `/concept-graph-stats${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`,
    ),
};
